/**
 * @fileoverview Mobile/tablet filter sheet with all filter controls
 * @module shared/components/filters/FilterSheet
 */

import { ReactElement, useState, useMemo, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarClock, Layers, Layers2 } from 'lucide-react'
import { type DateRange } from 'react-day-picker'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import { Button } from '@/shared/components/ui'
import { cn } from '@/shared/utils/cn'
import { useFilterSheet } from '@/shared/contexts/FilterSheetContext'
import { useFilter } from '@/shared/hooks/useFilter'
import { monthRange, isMonthInRange } from '@/shared/utils/dateUtils'
import { useMonthRangeDrag } from '@/shared/hooks/useMonthRangeDrag'
import type { AggregationType } from '@/shared/context/FilterContext'
import { AGGREGATION_TRANSLATION_KEYS, AGGREGATION_OPTIONS } from '@/shared/i18n/keyMaps'
import { pl, enUS } from 'date-fns/locale'

type FilterTab = 'preset' | 'calendar' | 'aggregation' | 'profitBasis'

/**
 * Full-screen filter sheet for mobile/tablet devices.
 * Contains all filter controls in a touch-friendly layout.
 */
export function FilterSheet(): ReactElement {
  const { t, i18n } = useTranslation('common')
  const { isOpen, activeSection, close } = useFilterSheet()
  const {
    startDate,
    endDate,
    setDateRange,
    aggregation,
    setAggregation,
    profitBasis,
    setProfitBasis,
  } = useFilter()

  // Derive active tab directly from activeSection (no state sync needed)
  const activeTab = useMemo((): FilterTab => {
    switch (activeSection) {
      case 'preset':
        return 'preset'
      case 'range':
        return 'calendar'
      case 'aggregation':
        return 'aggregation'
      case 'profitBasis':
        return 'profitBasis'
      default:
        return 'preset'
    }
  }, [activeSection])

  const [tempRange, setTempRange] = useState<DateRange | undefined>(undefined)
  const [clickCount, setClickCount] = useState<number>(0)

  // Sync calendar state when sheet opens - legitimate pattern for modal initialization
  /* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
  useEffect(() => {
    if (isOpen) {
      setTempRange(startDate && endDate ? { from: startDate, to: endDate } : undefined)
      setClickCount(0)
    }
  }, [isOpen, activeSection])
  /* eslint-enable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */

  const locale = i18n.language === 'pl' ? pl : enUS

  const handleDayClick = (day: Date): void => {
    if (clickCount === 0) {
      setTempRange({ from: day, to: undefined })
      setClickCount(1)
      return
    }
    if (clickCount === 1 && tempRange?.from) {
      const first = tempRange.from
      const second = day
      const from = second.getTime() < first.getTime() ? second : first
      const to = second.getTime() < first.getTime() ? first : second
      setTempRange({ from, to })
      const start = new Date(from)
      start.setHours(0, 0, 0, 0)
      const end = new Date(to)
      end.setHours(23, 59, 59, 999)
      setDateRange(start, end)
      close()
      setClickCount(0)
      return
    }
    setTempRange({ from: day, to: undefined })
    setClickCount(1)
  }

  // Month names for quick selection
  const monthNamesShort = [
    t('monthsShort.jan'),
    t('monthsShort.feb'),
    t('monthsShort.mar'),
    t('monthsShort.apr'),
    t('monthsShort.may'),
    t('monthsShort.jun'),
    t('monthsShort.jul'),
    t('monthsShort.aug'),
    t('monthsShort.sep'),
    t('monthsShort.oct'),
    t('monthsShort.nov'),
    t('monthsShort.dec'),
  ] as const

  const now = new Date()
  const currentMonthIndex = now.getMonth()

  // Detect whether the active range is exactly today / yesterday (quick-preset highlight)
  const currentTimeRange = (() => {
    const atStart = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate())
    const today = atStart(new Date())
    const startOnly = startDate ? atStart(startDate) : null
    const endOnly = endDate ? atStart(endDate) : null

    if (!startOnly || !endOnly) return 'custom'

    if (startOnly.getTime() === today.getTime() && endOnly.getTime() === today.getTime()) {
      return 'today'
    }

    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)
    if (startOnly.getTime() === yesterday.getTime() && endOnly.getTime() === yesterday.getTime()) {
      return 'yesterday'
    }

    return 'custom'
  })()

  const handleTimeRangeChange = useCallback(
    (value: string): void => {
      const now = new Date()
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const day =
        value === 'yesterday'
          ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
          : todayStart

      setDateRange(day, day)
      close()
    },
    [setDateRange, close]
  )

  const commitMonthSpan = useCallback(
    (loIdx: number, hiIdx: number): void => {
      const now = new Date()
      const year = now.getFullYear()
      const start = monthRange(year, loIdx, now).start
      const end = monthRange(year, hiIdx, now).end
      setDateRange(start, end)
      close()
    },
    [setDateRange, close]
  )

  const { dragRange, getMonthProps } = useMonthRangeDrag({ onCommit: commitMonthSpan })

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      close()
    }
  }

  const handleAggregationChange = (value: AggregationType): void => {
    setAggregation(value)
    close()
  }

  const handleProfitBasisChange = (value: 'OC' | 'CM2'): void => {
    setProfitBasis(value)
    close()
  }

  // Get title based on active section
  const getSheetTitle = (): string => {
    switch (activeTab) {
      case 'preset':
        return t('filters.sheetTitle.preset')
      case 'calendar':
        return t('filters.sheetTitle.dateRange')
      case 'aggregation':
        return t('filters.sheetTitle.aggregation')
      case 'profitBasis':
        return t('filters.sheetTitle.profitBasis')
      default:
        return t('filters.sheetTitle.preset')
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent side="left" className="w-[85vw] max-w-sm bg-[var(--glass-surface-dark)]">
        <SheetHeader className="pb-4">
          <SheetTitle>{getSheetTitle()}</SheetTitle>
          <SheetDescription className="sr-only">
            {t('filters.sheetDescription', { defaultValue: 'Select filter options' })}
          </SheetDescription>
        </SheetHeader>

        {/* Content based on active section */}
        <div className="px-4">
          {/* Preset tab */}
          {activeTab === 'preset' && (
            <div className="space-y-4">
              {/* Quick presets */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'today', label: t('timeRanges.today') },
                  { value: 'yesterday', label: t('timeRanges.yesterday') },
                ].map(preset => (
                  <Button
                    key={preset.value}
                    variant="outline"
                    className={cn(
                      'h-12 justify-start',
                      // Same input-glass selected tint as the month grid (bg-accent is
                      // overridden by the outline variant's dark:bg-input/* in dark mode).
                      currentTimeRange === preset.value &&
                        'bg-input text-foreground hover:bg-input hover:text-foreground dark:bg-input dark:hover:bg-input'
                    )}
                    onClick={() => handleTimeRangeChange(preset.value)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>

              {/* Month grid */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center justify-between gap-2">
                  {t('timeRanges.months')}
                  <span className="text-xs font-normal text-muted-foreground/50">
                    {t('timeRanges.dragHint')}
                  </span>
                </h4>
                <div className="grid grid-cols-3 gap-2 [touch-action:none] select-none">
                  {(() => {
                    const visibleMonths = monthNamesShort.slice(0, currentMonthIndex + 1)
                    const monthSelected = visibleMonths.map((_, idx) =>
                      dragRange
                        ? idx >= dragRange.lo && idx <= dragRange.hi
                        : isMonthInRange(idx, startDate, endDate, now.getFullYear(), now)
                    )
                    return visibleMonths.map((name, idx) => {
                      const isSelected = monthSelected[idx] ?? false
                      // Fuse adjacent selected pills (within a grid row) into one
                      // continuous band: extend each fused edge halfway across the 8px
                      // grid gap and keep a single facing border as a subtle divider.
                      const col = idx % 3
                      const fuseLeft =
                        isSelected && col !== 0 && (monthSelected[idx - 1] ?? false)
                      const fuseRight =
                        isSelected && col !== 2 && (monthSelected[idx + 1] ?? false)
                      return (
                        <Button
                          key={name}
                          variant="outline"
                          size="sm"
                          className={cn(
                            'h-10 relative',
                            // Subtle selected tint: full-strength input glass (2x the
                            // hover tint). The dark:-scoped classes are required so
                            // twMerge drops the outline variant's dark:bg-input/*
                            // (which otherwise hides the highlight).
                            isSelected &&
                              'bg-input text-foreground hover:bg-input hover:text-foreground dark:bg-input dark:hover:bg-input',
                            fuseLeft &&
                              'rounded-l-none -ml-1 border-l-foreground/25 dark:border-l-foreground/25',
                            fuseRight && 'rounded-r-none -mr-1 border-r-0'
                          )}
                          {...getMonthProps(idx)}
                        >
                          {name}
                          {idx === currentMonthIndex && (
                            <span
                              aria-hidden
                              className="absolute top-1 right-1 size-1 rounded-full bg-foreground/50"
                            />
                          )}
                        </Button>
                      )
                    })
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Calendar tab */}
          {activeTab === 'calendar' && (
            <CalendarComponent
              mode="range"
              selected={tempRange}
              onDayClick={handleDayClick}
              numberOfMonths={1}
              locale={locale}
              {...(startDate && { defaultMonth: startDate })}
              disabled={date => date > new Date() || date < new Date('1900-01-01')}
              className="w-full border-0 bg-transparent [--cell-size:2.5rem]"
            />
          )}

          {/* Aggregation tab */}
          {activeTab === 'aggregation' && (
            <div className="grid grid-cols-1 gap-2">
              {AGGREGATION_OPTIONS.map(option => (
                <Button
                  key={option}
                  variant="outline"
                  className={cn(
                    'h-12 justify-start',
                    aggregation === option && 'bg-accent text-accent-foreground'
                  )}
                  onClick={() => handleAggregationChange(option)}
                >
                  <CalendarClock className="w-4 h-4 mr-2" />
                  {t(AGGREGATION_TRANSLATION_KEYS[option])}
                </Button>
              ))}
            </div>
          )}

          {/* Profit basis tab */}
          {activeTab === 'profitBasis' && (
            <div className="grid grid-cols-1 gap-2">
              <Button
                variant="outline"
                className={cn(
                  'h-12 justify-start',
                  profitBasis === 'OC' && 'bg-accent text-accent-foreground'
                )}
                onClick={() => handleProfitBasisChange('OC')}
              >
                <Layers className="w-4 h-4 mr-2" />
                {t('filters.afterBusinessCosts', { defaultValue: 'After business costs' })}
              </Button>
              <Button
                variant="outline"
                className={cn(
                  'h-12 justify-start',
                  profitBasis === 'CM2' && 'bg-accent text-accent-foreground'
                )}
                onClick={() => handleProfitBasisChange('CM2')}
              >
                <Layers2 className="w-4 h-4 mr-2" />
                {t('filters.beforeBusinessCosts', { defaultValue: 'Before business costs' })}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
